import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { useGetCampaignReportSandboxQuery } from 'api/campaignReportSandbox'
import BarChart from 'components/Icons/BarChart'
import ChevronDown from 'components/Icons/ChevronDown'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import { buildTranslate } from 'locales'
import { Fields as Campaign } from 'models/Campaign'
import React, { useEffect, useMemo, useState } from 'react'
import { Container } from 'react-bootstrap-five'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import LookerIframe from 'scenes/LookerEmbed/LookerIframe'
import ReportModal from './ReportModal'

const t = buildTranslate('thanx_campaigns.report.v3.reports')

const DEFAULT_REPORT_ID = '462'

type Props = RouteComponentProps & {
  campaign: Campaign
}

const Reports: React.FC<Props> = ({ campaign, location }) => {
  const [css] = useStyletron()
  const merchant = useCurrentMerchant()

  // Parse query params from location
  const queryParams = useMemo(() => {
    return new URLSearchParams(location.search)
  }, [location.search])

  const [selectedReportId, setSelectedReportId] = useState<string>(() => {
    return queryParams.get('report') || DEFAULT_REPORT_ID
  })
  const [isSelectorOpen, setIsSelectorOpen] = useState(false)

  const { data: reports = [], isLoading } = useGetCampaignReportSandboxQuery(
    undefined,
    {
      skip: !merchant,
    }
  )

  // Validate and sync the selected report with query param (only on mount and when reports change)
  useEffect(() => {
    if (reports.length === 0) return

    const reportFromParam = queryParams.get('report')
    const reportExists = reports.some(report => report.id === reportFromParam)

    let targetReportId: string

    if (reportFromParam && reportExists) {
      // Use the valid query param
      targetReportId = reportFromParam
    } else {
      // Invalid or missing query param - default to 462, or first report if 462 doesn't exist
      const defaultExists = reports.some(
        report => report.id === DEFAULT_REPORT_ID
      )
      targetReportId = defaultExists ? DEFAULT_REPORT_ID : reports[0]?.id
    }

    // Update state if different
    if (selectedReportId !== targetReportId) {
      setSelectedReportId(targetReportId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports, queryParams])

  const selectedReport = reports.find(report => report.id === selectedReportId)

  if (!merchant) return null

  return (
    <Container fluid className="grey-05-bkg">
      <Container className="pt-l pb-xl">
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '24px',
            padding: '16px 24px',
            backgroundColor: 'white',
            border: '1px solid #E5E5E5',
            borderRadius: '4px',
          })}
        >
          <div
            className={css({
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            })}
          >
            <BarChart
              className={css({
                width: '48px',
                height: '48px',
                flexShrink: 0,
              })}
            />
            <div>
              <Text.SmallCaps3
                color="grey70"
                className={css({
                  marginBottom: '2px',
                  display: 'block',
                })}
              >
                {t('currently_viewing')}
              </Text.SmallCaps3>
              <Text.BodyText3 color="grey90">
                {selectedReport?.category || t('default_category')} &gt;{' '}
                {selectedReport?.title || t('default_dashboard_title')}
              </Text.BodyText3>
            </div>
          </div>
          <Button
            onClick={() => setIsSelectorOpen(prev => !prev)}
            disabled={isLoading}
            kind="primary"
            endEnhancer={() => <ChevronDown className="ml-xs" />}
          >
            {t('select_report')}
          </Button>
          <ReportModal
            isOpen={isSelectorOpen}
            onClose={() => setIsSelectorOpen(false)}
            reports={reports}
            selectedReportId={selectedReportId}
            onSelectReport={(reportId: string) => {
              setSelectedReportId(reportId)
              // Update URL without triggering React Router navigation
              const newParams = new URLSearchParams(location.search)
              newParams.set('report', reportId)
              window.history.replaceState(
                null,
                '',
                `${location.pathname}?${newParams.toString()}`
              )
              setIsSelectorOpen(false)
            }}
            isLoading={isLoading}
          />
        </div>

        <LookerIframe
          path={`dashboards/${selectedReportId}`}
          merchant={merchant}
          urlParams={{
            'Campaign ID': campaign.id.toString(),
            hide_filter: 'Campaign ID',
          }}
          height="80vh"
        />
      </Container>
    </Container>
  )
}

export default withRouter(Reports)
